import { Paperclip, ShieldAlert, Star } from "lucide-react";
import type { JSX, MouseEvent } from "react";
import { Avatar, AvatarFallback, cn } from "@mailflare/ui";
import type { MessageSummary } from "@mailflare/shared";

import { formatMessageDate, initialsFor } from "@/lib/utils";

export interface MessageRowProps {
  readonly message: MessageSummary;
  readonly selected: boolean;
  readonly focused: boolean;
  readonly onSelect: (message: MessageSummary) => void;
  readonly onToggleFlag: (message: MessageSummary) => void;
}

export function MessageRow({
  message,
  selected,
  focused,
  onSelect,
  onToggleFlag,
}: MessageRowProps): JSX.Element {
  const isUnread = !message.isRead;
  const isSpam = message.spamVerdict === "spam" || message.spamVerdict === "phishing";

  const handleFlag = (event: MouseEvent<HTMLButtonElement>): void => {
    event.stopPropagation();
    onToggleFlag(message);
  };

  return (
    <div
      role="option"
      aria-selected={selected}
      data-testid={`message-row-${message.id}`}
      onClick={() => onSelect(message)}
      className={cn(
        "border-border relative flex cursor-pointer items-start gap-3 border-b px-4 py-3 outline-none transition-colors",
        selected ? "bg-accent" : "hover:bg-accent/60",
        focused && "ring-ring ring-2 ring-inset",
      )}
    >
      <Avatar className="mt-0.5 size-9">
        <AvatarFallback>{initialsFor(message.from)}</AvatarFallback>
      </Avatar>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          {isUnread ? (
            <span
              role="img"
              aria-label="Unread"
              data-testid="unread-dot"
              className="bg-primary size-2 shrink-0 rounded-full"
            />
          ) : null}
          <span
            className={cn(
              "truncate text-sm",
              isUnread ? "text-foreground font-semibold" : "text-foreground",
            )}
          >
            {message.from.name ?? message.from.address}
          </span>
          {isSpam ? (
            <ShieldAlert
              role="img"
              aria-label="Marked as spam"
              className="text-warning size-3.5 shrink-0"
            />
          ) : null}
          {message.hasAttachments ? (
            <Paperclip
              role="img"
              aria-label="Has attachment"
              className="text-muted-foreground size-3.5 shrink-0"
            />
          ) : null}
          {message.isFlagged ? (
            <Star
              role="img"
              aria-label="Starred"
              className="fill-warning text-warning size-3.5 shrink-0"
            />
          ) : null}
          <time
            className="text-muted-foreground ml-auto shrink-0 text-xs"
            dateTime={message.receivedAt}
          >
            {formatMessageDate(message.receivedAt)}
          </time>
        </div>
        <p
          className={cn(
            "mt-0.5 truncate text-sm",
            isUnread ? "font-semibold" : "text-muted-foreground",
          )}
        >
          {message.subject.length > 0 ? message.subject : "(no subject)"}
        </p>
        <p className="text-muted-foreground mt-0.5 truncate text-xs">{message.snippet}</p>
      </div>

      <button
        type="button"
        aria-label={message.isFlagged ? "Remove star" : "Add star"}
        aria-pressed={message.isFlagged}
        onClick={handleFlag}
        className="focus-visible:ring-ring mt-0.5 shrink-0 rounded-md p-1 transition-colors hover:bg-accent focus-visible:ring-2 focus-visible:outline-none"
      >
        <Star
          aria-hidden
          className={cn(
            "size-4",
            message.isFlagged ? "fill-warning text-warning" : "text-muted-foreground/70",
          )}
        />
      </button>
    </div>
  );
}
